import {
  faKey,
  faUser,
  faEyeSlash,
  faEye,
  faEnvelopeOpen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import { toast } from 'react-hot-toast';
import { userValidationSchema } from 'utils/validationSchemas';
import { registerUser } from 'Api/userapi';
import { useState } from 'react';
import './style.css';

interface SignUpCredentials {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const SignUpForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setconfirmPasswordVisible] = useState(false);

  const handleSubmit = async (
    values: SignUpCredentials,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      const response = await registerUser(values);
      if (response.status === 201) {
        toast.success(`${response.data.message}`);
        navigate('/dashboard/properties');
        resetForm();
      }
    } catch (error: any) {
      console.error('Error during login:', error);

      if (error.response) {
        toast.error(`${error.response.data.message}`);
      } else {
        toast.error(
          'An error occurred during login. Please check your connection and try again.'
        );
      }
    }
  };

  return (
    <>
      <div className="text-center" >
        <Row className="g-3 mb-3">
          <Col xs={12} className="">
            <h3 className="fw-bold fs-4 fs-md-5  ">Sign up</h3>
            <p className="text-muted mb-2">
              100% money back guarantee
              <span className="fs-6 text-success"> as low as $30</span>/mo
            </p>
          </Col>
        </Row>
      </div>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          // phoneNumber: '',
          acceptTerms: false
        }}
        validationSchema={userValidationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, touched, errors, values }) => (
          <FormikForm>
            <Row className="g-3 mb-3 ">
              <Col sm={layout === 'card' ? 12 : 6} lg={6}>
                <Form.Group>
                  <div className="fs-9">First name</div>
                  <Field
                    name="firstName"
                    type="text"
                    className={`form-control form-icon-input ${
                      touched.firstName && errors.firstName ? 'is-invalid' : ''
                    }`}
                    placeholder="First name"
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className="text-danger mt-1 fs-9"
                  />
                </Form.Group>
              </Col>

              <Col sm={layout === 'card' ? 12 : 6} lg={6}>
                <Form.Group>
                  <div className="fs-9">Last name</div>
                  <Field
                    name="lastName"
                    type="text"
                    className={`form-control form-icon-input ${
                      touched.lastName && errors.lastName ? 'is-invalid' : ''
                    }`}
                    placeholder="Last name"
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className="text-danger mt-1 fs-9"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3 text-start">
              <div className="fs-9">Email address</div>
              <Field
                name="email"
                type="email"
                className={`form-control form-icon-input ${
                  touched.email && errors.email ? 'is-invalid' : ''
                }`}
                placeholder="name@example.com"
              />

              <ErrorMessage
                name="email"
                component="div"
                className="text-danger mt-1 fs-9"
              />
            </Form.Group>

            <div className="mb-3 position-relative">
              <label className="fs-9">Password</label>
              <Field
                name="password"
                type={passwordVisible ? 'text' : 'password'}
                className={`form-control ${
                  errors.password && touched.password ? 'is-invalid' : ''
                }`}
                placeholder="Password"
              />
              <FontAwesomeIcon
                icon={passwordVisible ? faEye : faEyeSlash}
                className="eye-icon"
                onClick={() => setPasswordVisible(!passwordVisible)}
              />
              <ErrorMessage
                name="password"
                component="div"
                className="text-danger mt-1 fs-9"
              />
            </div>
            <div className="mb-3 position-relative">
              <label className="fs-9">Confirm password</label>
              <Field
                name="confirmPassword"
                type={confirmPasswordVisible ? 'text' : 'password'}
                className={`form-control ${
                  errors.password && touched.password ? 'is-invalid' : ''
                }`}
                placeholder="Password"
              />
              <FontAwesomeIcon
                icon={confirmPasswordVisible ? faEye : faEyeSlash}
                className="eye-icon"
                onClick={() =>
                  setconfirmPasswordVisible(!confirmPasswordVisible)
                }
              />
              <ErrorMessage
                name="confirmPassword"
                component="div"
                className="text-danger mt-1 fs-9"
              />
            </div>
            <Form.Group className="mb-4 text-start">
              <div className="form-check">
                <Field
                  type="checkbox"
                  name="acceptTerms"
                  className="form-check-input"
                  id="acceptTerms"
                />
                <label className="form-check-label" htmlFor="acceptTerms">
                  I accept the <a href="/terms-of-use">Terms of Use</a> &{' '}
                  <a href="/privacy-policy">Privacy Policy</a>.
                </label>
              </div>
              <ErrorMessage
                name="acceptTerms"
                component="div"
                className="text-danger mt-1 fs-9"
              />
            </Form.Group>

            <div className="text-center">
              <Button
                variant="primary"
                className="mb-3"
                type="submit"
                disabled={isSubmitting || !values.acceptTerms}
              >
                Create Account
              </Button>
            </div>
            <div className=" mb-3 mt-2">
              <Col xs="auto" className="text-center text-xs-small">
                Already a member?&nbsp;
                <Link
                  to={`/sign-in`}
                  className="fs-16 text-decoration-none text-xs-small"
                >
                  Sign in
                </Link>
                &nbsp;
                <span>|</span>&nbsp;
                <Link
                  to={'/forgot-password'}
                  className="text-decoration-none text-xs-small"
                >
                  Recover password
                </Link>
              </Col>
            </div>
          </FormikForm>
        )}
      </Formik>
    </>
  );
};

export default SignUpForm;
